import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { toast } from 'sonner';
import type { AgencyMember, Role } from '@/types';

interface AddMemberDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  editMember?: AgencyMember | null;
  /** SystemAdmin may additionally grant the SystemAdmin role. */
  canAssignSystemAdmin?: boolean;
}

export function AddMemberDialog({
  open,
  onClose,
  tenantId,
  editMember,
  canAssignSystemAdmin = false,
}: AddMemberDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = !!editMember;

  const roleOptions: { value: Role; label: string }[] = [
    { value: 'Agent', label: 'סוכן' },
    { value: 'AgencyAdmin', label: 'מנהל סוכנות' },
    ...(canAssignSystemAdmin ? [{ value: 'SystemAdmin' as Role, label: 'מנהל מערכת' }] : []),
  ];

  const [form, setForm] = useState<{ name: string; email: string; mobile: string; role: Role }>({
    name: '',
    email: '',
    mobile: '',
    role: 'Agent',
  });

  useEffect(() => {
    if (editMember) {
      setForm({
        name: editMember.name || '',
        email: editMember.email || editMember.username,
        mobile: editMember.mobile || '',
        role: editMember.role,
      });
    } else {
      setForm({ name: '', email: '', mobile: '', role: 'Agent' });
    }
  }, [editMember, open]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['members', tenantId] });

  const addMutation = useMutation({
    mutationFn: () =>
      api.addMember(tenantId, {
        email: form.email.trim(),
        name: form.name.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        role: form.role,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('המשתמש נוסף בהצלחה. נשלחה הזמנה למייל.');
      onClose();
    },
    onError: (err: Error) => toast.error('שגיאה', { description: err.message }),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.updateMember(tenantId, editMember!.user_id, {
        name: form.name.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        role: form.role,
      }),
    onSuccess: () => {
      invalidate();
      toast.success('המשתמש עודכן בהצלחה');
      onClose();
    },
    onError: (err: Error) => toast.error('שגיאה בעדכון', { description: err.message }),
  });

  const pending = addMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('יש להזין שם מלא');
    if (!form.email.trim()) return toast.error('יש להזין אימייל');
    if (isEdit) updateMutation.mutate();
    else addMutation.mutate();
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ width: 520 }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2>{isEdit ? 'עריכת משתמש' : 'הוספת משתמש חדש'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="סגור">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="modal-body">
          <p className="import-intro">
            {isEdit
              ? 'עדכון פרטי המשתמש והתפקיד שלו בסוכנות.'
              : 'הזינו את פרטי המשתמש. תישלח אליו הזמנה במייל עם סיסמה זמנית (רק אם אין לו כבר חשבון).'}
          </p>

          <div className="modal-grid">
            <label className="lead-field">
              <span className="lead-field-label">שם מלא *</span>
              <input value={form.name} placeholder="הזינו שם מלא" autoFocus onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </label>

            <label className="lead-field">
              <span className="lead-field-label">אימייל *</span>
              <input type="email" dir="ltr" value={form.email} placeholder="user@example.com" disabled={isEdit} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </label>

            <label className="lead-field">
              <span className="lead-field-label">טלפון נייד</span>
              <input type="tel" dir="ltr" value={form.mobile} placeholder="05X-XXXXXXX" onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))} />
            </label>

            <label className="lead-field">
              <span className="lead-field-label">תפקיד</span>
              <select value={form.role} onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as Role }))}>
                {roleOptions.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
          </div>

          {isEdit && <p className="import-ignored" style={{ marginTop: 8 }}>לא ניתן לשנות כתובת אימייל לאחר יצירת המשתמש.</p>}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn outline" onClick={onClose} disabled={pending}>ביטול</button>
          <div className="spacer" />
          <button type="submit" className="btn" disabled={pending || !form.name.trim() || !form.email.trim()}>
            {pending ? 'שומר…' : isEdit ? 'עדכן' : 'הוסף משתמש'}
          </button>
        </div>
      </form>
    </div>
  );
}
